import { getTrialDaysLeft } from './trialDeadline';
/**
 * The one sentence every surface shows when the trial has ended. The extension's
 * lapsed banner, the dashboard chip and the billing page all render THIS, so the
 * wording cannot drift three ways again (extension said "Your free plan ended."
 * until 2026-09-01; no free plan exists, `free` is the null-plan fallback).
 */
export const TRIAL_ENDED_COPY = 'Your trial ended. Your work is saved.';
/**
 * The final-date label. The header chip, the billing page's Current Plan card
 * and the account panel all render THIS on the last day of a trial, never
 * "0 days left": the count is the whole dates after today, so 0 is a live day.
 */
export const TRIAL_ENDS_TODAY_LABEL = 'Ends today';
/**
 * What a lapsed trial PAUSES, one list for the trial-conversion emails
 * (packages/server/src/services/email/senders/trialConversion.ts) and the
 * billing page's Current Plan card, so the two surfaces cannot describe the
 * lapse differently. Every item is a route behind checkWriteAccess
 * (packages/server/src/middleware/planLimits.ts), which refuses writes only, so
 * saved work stays viewable; the intro sentence says so. Added 2026-10-06 after
 * a trialing owner read the billing page's "Team plan · Active" as a paid
 * plan and asked whether any action was needed; the page never said what the
 * trial's end would pause.
 */
export const TRIAL_PAUSE_ITEMS = [
    'Capturing new issues',
    'Editing issues',
    'Gathering team and client feedback: comments and Client Links',
    'Exporting to Jira, Linear and your other tools',
    'Visual comparison, accessibility and token scans',
];
export const TRIAL_PAUSES_INTRO = "When the trial ends, everything you've saved stays in your workspace and stays viewable. What pauses:";
/**
 * Self-serve trial extension copy (Emily, 2026-09-24), one source for the
 * dashboard, the extension and team review so the three cannot drift. The
 * server rules live in packages/server/src/lib/trialSelfExtend.ts.
 */
export const TRIAL_SELF_EXTEND_CTA = 'Extend my trial 7 days';
export const TRIAL_SELF_EXTEND_HINT = 'You can extend once, and it starts today.';
export const TRIAL_SELF_EXTEND_ASK_OWNER = 'Ask your team owner to extend the trial by 7 days.';
export const TRIAL_SELF_EXTEND_SUCCESS = 'Trial extended. You have 7 more days.';
// A refusal a retry cannot fix (already extended, outside the window, paid before).
export const TRIAL_SELF_EXTEND_UNAVAILABLE = 'This trial can no longer be extended. Pick a plan to keep going.';
export const TRIAL_SELF_EXTEND_FAILED = 'We could not extend your trial. Try again in a moment.';
/**
 * The trial-ended VALUE ask, as opposed to TRIAL_ENDED_COPY's one-line status.
 *
 * These three strings shipped inside UpgradeModal's COPY.trial_expired entry
 * and were reachable only through a 403. The client-side trial lock gates at
 * the create ENTRY POINT (extension lib/projectViewOnly.ts), so a trial-locked
 * user never fires the request that would 403, and never saw them. They live
 * here so the modal and the two capture-wall gates render ONE sentence: the
 * extension already drifted this copy three ways once (see TRIAL_ENDED_COPY).
 */
export const TRIAL_ENDED_GATE_TITLE = 'Your trial has ended';
export const TRIAL_ENDED_GATE_BODY = "Your work is saved and won't be deleted. Upgrade to keep capturing issues, running audits, and exporting.";
export const TRIAL_ENDED_GATE_CTA = 'See plans';
const WARNING_DAYS_THRESHOLD = 2;
const ENDING_TODAY_HOURS_THRESHOLD = 24;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export function getTrialState(input) {
    const { plan, betaExpiresAt, isOwner, trialTimeZone, now = Date.now() } = input;
    // Non-owners can't upgrade the team. Showing them a nudge they can't act on
    // is worse than silence — they'll just feel confused or pestered.
    if (!isOwner)
        return 'paid';
    // Plan null = trial ended OR subscription lapsed. Always show expired banner.
    if (plan === null)
        return 'expired';
    // Has a plan slug. Is it a real paid plan or a beta-trial 'team'?
    // Distinguish by checking betaExpiresAt: if it's in the future, this is a
    // trial (the beta safety net in billing.service.ts is returning 'team'
    // because betaExpiresAt > now, not because the user actually paid).
    //
    // That inference is sound ONLY because the server withholds betaExpiresAt
    // whenever the plan came from an active Clerk subscription (getTeamBillingInfo
    // in billing.service.ts, gated on plan provenance since 2026-09-08). A paid
    // subscriber with leftover trial days therefore never reaches this branch
    // with a date; before that gate they did, and saw "11d left · See plans"
    // for the rest of a trial they had already converted.
    if (!betaExpiresAt)
        return 'paid';
    const expiresAtMs = new Date(betaExpiresAt).getTime();
    if (!Number.isFinite(expiresAtMs))
        return 'paid'; // unparseable → don't nudge
    if (expiresAtMs <= now)
        return 'expired'; // beta passed but plan
    //   still 'team' is a
    //   cache race; treat as
    //   expired for nudge UI
    const msLeft = expiresAtMs - now;
    if (trialTimeZone) {
        // Whole dates still ahead of today: 0 is the final date, 1 means the trial
        // ends tomorrow night (the 48h warning window), anything more is calm.
        const days = getTrialDaysLeft(betaExpiresAt, trialTimeZone, now);
        if (days === 0)
            return 'ending-today';
        return days < WARNING_DAYS_THRESHOLD ? 'warning' : 'safe';
    }
    if (msLeft < ENDING_TODAY_HOURS_THRESHOLD * HOUR_MS)
        return 'ending-today';
    if (msLeft <= WARNING_DAYS_THRESHOLD * DAY_MS)
        return 'warning';
    // Any remaining runway beyond 48h is the calm state. There is deliberately no
    // upper bound here: a trial with 14 days left is still a trial, and silence
    // is what let users discover the clock only once it had run out.
    return 'safe';
}
/**
 * Render the "· N issues captured" clause, or '' when it must not appear.
 *
 * This reports what the user has BUILT. It is deliberately NOT a quota, and
 * must never be phrased as "N left": an OverlayQA trial resolves to plan
 * 'team' (billing.service.ts, the betaExpiresAt safety net), and team is
 * `maxIssuesPerProject: -1` in planLimitsConfig.ts:36 — unlimited. There is no
 * issue meter to count down, so wording it as one would advertise a limit the
 * product does not enforce and has never sold.
 *
 * Zero is omitted on purpose: "0 issues captured" beside a countdown tells a
 * first-day user they are already behind, which is the opposite of the intent.
 */
function capturedClause(issuesCaptured) {
    if (typeof issuesCaptured !== 'number')
        return '';
    if (!Number.isFinite(issuesCaptured) || issuesCaptured < 1)
        return '';
    const n = Math.floor(issuesCaptured);
    return ` · ${n} issue${n === 1 ? '' : 's'} captured`;
}
/**
 * Convert trial state to user-facing copy. Owners only — non-owners get 'paid'
 * from getTrialState and never see these strings.
 *
 * `issuesCaptured` is the team's non-demo ticket count. It decorates the calm
 * state only; see capturedClause for why, and why it is not a quota.
 */
export function getTrialNudgeCopy(state, hoursLeft, issuesCaptured, daysLeft) {
    switch (state) {
        case 'safe': {
            // The calm, always-on state: the whole trial until the final 48h.
            const days = daysLeft ?? Math.ceil(hoursLeft / 24);
            const captured = capturedClause(issuesCaptured);
            return {
                // With a count this reads "15 days left · 24 issues captured"; without
                // one it falls back to the original sentence, so a failed or still
                // loading count degrades to the copy that shipped before.
                title: captured
                    ? `${days} day${days === 1 ? '' : 's'} left${captured}`
                    : `${days} day${days === 1 ? '' : 's'} left in your trial.`,
                cta: 'See plans',
                variant: 'info',
            };
        }
        case 'warning': {
            const days = daysLeft ?? Math.ceil(hoursLeft / 24);
            return {
                title: `Trial ends in ${days} day${days === 1 ? '' : 's'}. Pick a plan to keep capturing.`,
                cta: 'Pick a plan',
                variant: 'warning',
            };
        }
        case 'ending-today':
            return {
                title: 'Trial ends today. Pick a plan to keep capturing.',
                cta: 'Pick a plan',
                variant: 'urgent',
            };
        case 'expired':
            return {
                title: `${TRIAL_ENDED_COPY} Pick a plan to keep capturing.`,
                cta: 'Pick a plan',
                variant: 'expired',
            };
        case 'paid':
        default:
            return null;
    }
}
/**
 * Compact copy for the dashboard's HEADER CHIP, the small link that rides the
 * existing top row before the notification bell (Emily, 2026-08-31: "smaller
 * width on the same row as notifications and avatar not a full dismissable
 * banner").
 *
 * Two constraints shape every string here, and both are load-bearing:
 *
 * 1. IT MUST STAY SHORT. The chip sits between the page breadcrumb and two
 *    40px controls, so it is measured, not eyeballed: a test pins every label
 *    at <= 24 characters, including the 4-digit-count case.
 * 2. IT MUST NOT READ AS A QUOTA. "24 captured" keeps its verb for the same
 *    reason the full banner does — a trial is maxIssuesPerProject: -1
 *    (unlimited), so a bare "24 issues" would advertise an allowance that does
 *    not exist. See capturedClause.
 *
 * Returns null for `expired` as well as `paid`. Expired is not a nudge: the
 * workspace is read-only and every write 403s, so it keeps the full-width bar
 * (which was never dismissible) rather than shrinking into a corner chip a
 * user meets only after their first refused save.
 */
export function getTrialChipCopy(state, hoursLeft, issuesCaptured, daysLeft) {
    const days = daysLeft ?? Math.ceil(hoursLeft / 24);
    const full = getTrialNudgeCopy(state, hoursLeft, issuesCaptured, daysLeft);
    // CTA wording is taken from the banner copy rather than restated, so the
    // chip and the expired bar can never disagree about what the action is
    // called. Calm reads "See plans"; the final 48h reads "Pick a plan".
    const cta = full?.cta ?? 'See plans';
    switch (state) {
        case 'safe': {
            // `capturedClause` already returns " · N issues captured"; the chip drops
            // the noun to save ~7 characters and keeps the verb, which is the half
            // that carries the meaning.
            const clause = capturedClause(issuesCaptured).replace(' issues captured', ' captured').replace(' issue captured', ' captured');
            return {
                label: `${days}d left${clause}`,
                cta,
                title: full?.title ?? `${days} days left in your trial.`,
                variant: 'info',
            };
        }
        case 'warning':
            // No count in the final 48h. The chip has one job then, and a capture
            // tally competes with the only line the user needs to read.
            return { label: `${days}d left`, cta, title: full?.title ?? '', variant: 'warning' };
        case 'ending-today':
            // Never "0d left": the arithmetic is right and it reads as expired.
            return { label: TRIAL_ENDS_TODAY_LABEL, cta, title: full?.title ?? '', variant: 'urgent' };
        default:
            return null;
    }
}
/** Convenience for UIs that want a single "hours until expiry" number. */
export function getTrialHoursLeft(betaExpiresAt, now = Date.now()) {
    if (!betaExpiresAt)
        return 0;
    const ms = new Date(betaExpiresAt).getTime();
    if (!Number.isFinite(ms))
        return 0;
    return Math.max(0, (ms - now) / HOUR_MS);
}
//# sourceMappingURL=trialState.js.map